/* KIRAKI — A Sunday, scrolled. Motion + interaction engine.
   GSAP (ScrollTrigger, SplitText) for scroll choreography, Motion for springs,
   Lenis for smooth scroll. Everything degrades to a calm static page. */
(function () {
  'use strict';

  var html = document.documentElement;
  var KS = (window.KS = window.KS || {});
  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var M = window.Motion || null;
  var motion = html.classList.contains('ks-motion') && hasGsap;
  var finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  var lenis = null;
  var contexts = new Map();

  KS.ready = false;

  /* ---------------------------------------------------------------- utils */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function toMinutes(str) {
    if (!str) return null;
    var m = String(str).trim().match(/^(\d{1,2}):(\d{2})$/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  }
  // U.S. 12-hour time: 1050 -> "5:30 pm". Pass big=true for large numbers (am/pm in a smaller span).
  function fmtMinutes(min, big) {
    min = ((Math.round(min) % 1440) + 1440) % 1440;
    var h = Math.floor(min / 60), ap = h >= 12 ? 'pm' : 'am';
    var t = (h % 12 || 12) + ':' + pad(min % 60);
    return big ? t + '<span class="ks-ampm">' + ap + '</span>' : t + ' ' + ap;
  }
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(hex, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function toLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function toSrgb(c) { c = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; return clamp(Math.round(c * 255), 0, 255); }
  function rgbToLab(c) {
    var r = toLin(c[0]), g = toLin(c[1]), b = toLin(c[2]);
    var l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    var m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    var s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
  }
  function labToRgb(L) {
    var l = Math.pow(L[0] + 0.3963377774 * L[1] + 0.2158037573 * L[2], 3);
    var m = Math.pow(L[0] - 0.1055613458 * L[1] - 0.0638541728 * L[2], 3);
    var s = Math.pow(L[0] - 0.0894841775 * L[1] - 1.291485548 * L[2], 3);
    return [toSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s), toSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s), toSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)];
  }
  function mixRgb(a, b, t) {
    var A = rgbToLab(a), B = rgbToLab(b);
    var c = labToRgb([lerp(A[0], B[0], t), lerp(A[1], B[1], t), lerp(A[2], B[2], t)]);
    return 'rgb(' + c[0] + ' ' + c[1] + ' ' + c[2] + ')';
  }
  function formatMoney(cents) {
    var fmt = KS.moneyFormat || '${{amount}}';
    var value = (cents / 100);
    var withDelims = function (num, dec, thousands, decimal) {
      var parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(dec ? decimal : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }
  function spring(el, keyframes, opts) {
    if (M && M.animate && motion) {
      return M.animate(el, keyframes, Object.assign({ type: 'spring', stiffness: 380, damping: 32 }, opts || {}));
    }
    var last = {};
    Object.keys(keyframes).forEach(function (k) {
      var v = keyframes[k];
      last[k] = Array.isArray(v) ? v[v.length - 1] : v;
    });
    if (window.gsap) gsap.set(el, last);
    return null;
  }
  KS.spring = spring;

  /* ---------------------------------------------------------------- tones + clock */
  var TONES = {
    morning: { bg: '#e5e1d8', fg: '#22211e' },
    noon: { bg: '#ece9e2', fg: '#22211e' },
    golden: { bg: '#dccaa8', fg: '#2a251c' },
    dusk: { bg: '#333946', fg: '#e8e3d8' },
    night: { bg: '#12161d', fg: '#e8e3d8' }
  };
  Object.keys(TONES).forEach(function (k) { TONES[k].bgRgb = hexToRgb(TONES[k].bg); TONES[k].fgRgb = hexToRgb(TONES[k].fg); });

  // Keep the browser's top bar (iOS Safari status area, theme-color, overscroll) the same colour
  // as whatever is directly under the header: the hero photo, the current hour's page tone, or the footer.
  var themeMeta = null, lastTop = '';
  function syncTop() {
    if (!themeMeta) themeMeta = document.querySelector('meta[name="theme-color"]');
    var c;
    if (html.hasAttribute('data-ks-hero-under')) c = '#8a8583';
    else if (html.hasAttribute('data-ks-footer-under')) c = '#12161d';
    else c = getComputedStyle(document.body).getPropertyValue('--ks-bg').trim() || getComputedStyle(document.body).backgroundColor;
    if (!c || c === lastTop) return;
    lastTop = c;
    if (themeMeta) themeMeta.setAttribute('content', c);
    html.style.backgroundColor = c;
  }

  var Day = {
    markers: [],
    times: [],
    clockEl: null,
    lastBg: '',
    lastClock: '',
    realClockTimer: null,
    init: function () {
      this.clockEl = $('[data-ks-clock-time]');
      var dayEl = $('[data-ks-clock-day]');
      if (dayEl) {
        try { dayEl.textContent = new Date().toLocaleDateString(undefined, { weekday: 'long' }); } catch (e) { dayEl.textContent = 'Today'; }
      }
      this.measure();
      if (this.markers.length) html.classList.add('ks-day');
      if (!this.times.length) this.startRealClock();
      this.update();
    },
    measure: function () {
      var y = window.scrollY;
      var all = $$('main [data-ks-tone]');
      this.markers = all.map(function (el) {
        var tone = TONES[el.getAttribute('data-ks-tone')] || TONES.morning;
        return { el: el, top: el.getBoundingClientRect().top + y, tone: tone };
      }).sort(function (a, b) { return a.top - b.top; });
      var vh = window.innerHeight;
      this.times = all.filter(function (el) { return toMinutes(el.getAttribute('data-ks-time')) !== null; }).map(function (el) {
        var r = el.getBoundingClientRect();
        // A section 'owns' the clock from when it fills the upper part of the view.
        return { top: r.top + y + Math.min(r.height, vh) * 0.35, min: toMinutes(el.getAttribute('data-ks-time')) };
      }).sort(function (a, b) { return a.top - b.top; });
    },
    update: function () {
      var probe = window.scrollY + window.innerHeight * 0.62;
      var mk = this.markers;
      if (mk.length) {
        var i = 0;
        for (var k = 0; k < mk.length; k++) if (mk[k].top <= probe) i = k;
        var a = mk[i];
        var b = mk[i + 1] || a;
        var t = 0;
        if (b !== a) {
          var span = b.top - a.top;
          var zone = Math.min(window.innerHeight * 0.55, span * 0.6);
          t = clamp((probe - (b.top - zone)) / zone, 0, 1);
          t = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        }
        if (probe < mk[0].top) { a = b = mk[0]; t = 0; }
        var bg = mixRgb(a.tone.bgRgb, b.tone.bgRgb, t);
        if (bg !== this.lastBg) {
          this.lastBg = bg;
          // Text flips quickly at the midpoint so it never sits grey-on-grey.
          var ft = clamp((t - 0.42) / 0.16, 0, 1);
          document.body.style.setProperty('--ks-bg', bg);
          document.body.style.setProperty('--ks-fg', mixRgb(a.tone.fgRgb, b.tone.fgRgb, ft));
          html.toggleAttribute('data-ks-dark', lerp(a.tone.bgRgb[0], b.tone.bgRgb[0], t) < 120);
        }
      }
      var foot = this.footEl || (this.footEl = $('[data-ks-footer]'));
      if (foot) html.toggleAttribute('data-ks-footer-under', foot.getBoundingClientRect().top < 70);
      syncTop();
      var tm = this.times;
      if (tm.length && this.clockEl) {
        var j = 0;
        for (var n = 0; n < tm.length; n++) if (tm[n].top <= probe) j = n;
        var p = tm[j], q = tm[j + 1];
        var min = p.min;
        if (probe < tm[0].top) min = tm[0].min;
        else if (q) {
          // Hold each hour for a while, then let time pass on the way to the next.
          var f = clamp((probe - p.top) / (q.top - p.top), 0, 1);
          min = lerp(p.min, q.min, clamp((f - 0.35) / 0.65, 0, 1));
        }
        var label = fmtMinutes(min);
        if (label !== this.lastClock) { this.lastClock = label; this.clockEl.textContent = label; }
      }
    },
    startRealClock: function () {
      var el = this.clockEl;
      if (!el) return;
      var tick = function () { var d = new Date(); el.textContent = fmtMinutes(d.getHours() * 60 + d.getMinutes()); };
      tick();
      this.realClockTimer = setInterval(tick, 15000);
    }
  };
  KS.day = Day;

  /* ---------------------------------------------------------------- smooth scroll */
  function initLenis() {
    if (!motion || !KS.smooth || typeof window.Lenis !== 'function' || KS.designMode) return;
    lenis = new window.Lenis({ lerp: 0.11, smoothWheel: true, wheelMultiplier: 0.95, touchMultiplier: 1, prevent: function (node) { return !!(node && node.closest && node.closest('dialog, [data-lenis-prevent], .theme-drawer__dialog')); } });
    KS.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    var sync = function () {
      var locked = html.hasAttribute('scroll-lock') || !!document.querySelector('dialog[open]:modal, .ks-menu[open]');
      if (locked && !lenis.isStopped) lenis.stop();
      else if (!locked && lenis.isStopped) lenis.start();
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });
    new MutationObserver(sync).observe(html, { attributes: true, attributeFilter: ['scroll-lock'] });
    sync();
  }

  function scrollToTarget(target) {
    var offset = -(parseInt(getComputedStyle(html).getPropertyValue('--ks-header'), 10) || 68) - 8;
    if (lenis) lenis.scrollTo(target, { offset: offset, duration: 1.0, easing: function (t) { return 1 - Math.pow(1 - t, 4); } });
    else {
      var top = target.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({ top: top, behavior: motion ? 'smooth' : 'auto' });
    }
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-ks-anchor]');
    if (!a) return;
    var id = a.getAttribute('href').split('#')[1];
    var target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    scrollToTarget(target);
    history.replaceState(null, '', '#' + id);
  });

  /* ---------------------------------------------------------------- header */
  function initHeader() {
    var header = $('[data-ks-header]');
    if (!header) return;
    var lastY = window.scrollY;
    var onScroll = function () {
      var y = window.scrollY;
      header.toggleAttribute('data-scrolled', y > 24);
      var goingDown = y > lastY + 4;
      var goingUp = y < lastY - 4;
      if (false) header.setAttribute('data-hidden', '');
      else if (goingUp || y < 120) header.removeAttribute('data-hidden');
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    $$('[data-ks-panel]', header).forEach(function (item) {
      var link = $('.ks-nav__link', item);
      var set = function (open) {
        item.toggleAttribute('data-open', open);
        link.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && motion) {
          var products = $$('.ks-panel__product', item);
          gsap.fromTo(products, { y: 6, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, stagger: 0.03, ease: 'power3.out', overwrite: true });
        }
      };
      var closeTimer;
      item.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { clearTimeout(closeTimer); set(true); } });
      item.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') closeTimer = setTimeout(function () { set(false); }, 120); });
      item.addEventListener('focusin', function () { set(true); });
      item.addEventListener('focusout', function (e) { if (!item.contains(e.relatedTarget)) set(false); });
      item.addEventListener('keydown', function (e) { if (e.key === 'Escape') { set(false); link.focus(); } });
    });

    var menu = $('[data-ks-menu]');
    var openBtn = $('[data-ks-menu-open]', header);
    if (menu && openBtn) {
      openBtn.addEventListener('click', function () {
        menu.showModal();
        openBtn.setAttribute('aria-expanded', 'true');
        if (motion) {
          gsap.fromTo($$('.ks-menu__link', menu), { yPercent: 30, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4, stagger: 0.03, ease: 'power3.out' });
          gsap.fromTo($$('.ks-menu__sub, .ks-menu__foot', menu), { opacity: 0 }, { opacity: 1, duration: 0.35, delay: 0.1, ease: 'power2.out' });
          gsap.fromTo(menu, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 0.45, ease: 'power3.out' });
        }
      });
      var close = function () {
        if (!menu.open) return;
        if (motion) {
          gsap.to(menu, { clipPath: 'inset(0 0 100% 0)', duration: 0.3, ease: 'power3.in', onComplete: function () { menu.close(); gsap.set(menu, { clearProps: 'clipPath' }); } });
        } else menu.close();
        openBtn.setAttribute('aria-expanded', 'false');
      };
      $$('[data-ks-menu-close]', menu).forEach(function (b) { b.addEventListener('click', close); });
      menu.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
      menu.addEventListener('click', function (e) { if (e.target.closest('a')) setTimeout(close, 10); });
    }
  }

  /* ---------------------------------------------------------------- hero: the window opens */
  function initHero(root) {
    var hero = $('[data-ks-hero]', root);
    if (!hero) return;
    var stage = $('.ks-hero__stage', hero);
    var win = $('[data-ks-window]', hero);
    var media = $('[data-ks-hero-media]', hero);
    var title = $('[data-ks-hero-title]', hero);
    var fades = $$('[data-ks-hero-fade]', hero);
    var overlay = $('[data-ks-hero-overlay]', hero);
    var copy = $('.ks-hero__copy', hero);
    var schedule = $('.ks-schedule', hero);
    if (hero.hasAttribute('data-ks-hero-full')) { initHeroFull(hero, media, title, fades, copy); return; }
    if (!motion || !stage || !win || !media) { html.classList.remove('ks-boot'); return; }

    hero.setAttribute('data-ks-hero-ready', '');
    var box = { t: 0, r: 0, b: 0, l: 0, w: 0, h: 0 };
    var state = { intro: 0, open: 0 };

    function measure() {
      if (window.innerWidth < 900 && copy) {
        var st = stage.getBoundingClientRect(), cp = copy.getBoundingClientRect();
        stage.style.setProperty('--ks-win-h', Math.max(220, st.bottom - cp.bottom - 48) + 'px');
      } else stage.style.removeProperty('--ks-win-h');
      var s = stage.getBoundingClientRect();
      var w = win.getBoundingClientRect();
      box = { t: w.top - s.top, l: w.left - s.left, r: s.right - w.right, b: s.bottom - w.bottom, w: w.width, h: w.height };
      var focus = parseFloat(hero.getAttribute('data-ks-focus'));
      box.shift = isNaN(focus) ? 0 : (box.l + box.w / 2) - s.width * focus / 100;
      var focusY = parseFloat(hero.getAttribute('data-ks-focus-y'));
      box.shiftY = isNaN(focusY) ? 0 : (box.t + box.h * 0.5) - s.height * focusY / 100;
    }
    function draw() {
      var o = state.open;
      var oe = o < 0.5 ? 4 * o * o * o : 1 - Math.pow(-2 * o + 2, 3) / 2;
      var half = box.w / 2;
      var shut = (1 - state.intro) * half;
      var t = lerp(box.t + (1 - state.intro) * box.h * 0.06, 0, oe);
      var l = lerp(box.l + shut, 0, oe);
      var r = lerp(box.r + shut, 0, oe);
      var b = lerp(box.b, 0, oe);
      var rad = hero.hasAttribute('data-ks-frame') ? lerp(3, 0, oe) : lerp(Math.max(half - shut, 2), 0, oe);
      var rb = hero.hasAttribute('data-ks-frame') ? rad : 2;
      media.style.setProperty('--ks-clip', 'inset(' + t.toFixed(1) + 'px ' + r.toFixed(1) + 'px ' + b.toFixed(1) + 'px ' + l.toFixed(1) + 'px round ' + rad.toFixed(1) + 'px ' + rad.toFixed(1) + 'px ' + rb.toFixed(1) + 'px ' + rb.toFixed(1) + 'px)');
      media.style.setProperty('--ks-img-scale', (1.14 - 0.06 * state.intro - 0.08 * oe).toFixed(4));
      media.style.setProperty('--ks-img-shift', ((1 - oe) * (box.shift || 0)).toFixed(1) + 'px');
      media.style.setProperty('--ks-img-shift-y', ((1 - oe) * (box.shiftY || 0)).toFixed(1) + 'px');
    }
    measure();
    draw();

    var ctx = gsap.context(function () {
      // Everything waits in its starting state, so nothing is drawn twice.
      // Headline, label, text and buttons are revealed by CSS at first paint (micro motion, no JS wait).

      var play = function () {
        if (hero.__ksPlayed) return;
        hero.__ksPlayed = true;
        html.classList.remove('ks-boot');

        var intro = gsap.timeline({ delay: 0.05 });
        intro.to(state, { intro: 1, duration: 0.9, ease: 'expo.out', onUpdate: draw }, 0);
      };
      var fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      var heroImg = media.querySelector('img');
      var imgReady = heroImg && heroImg.decode ? heroImg.decode().catch(function () {}) : Promise.resolve();
      Promise.race([Promise.all([fontsReady, imgReady]), new Promise(function (r) { setTimeout(r, 250); })]).then(function () { requestAnimationFrame(play); });

      // Scroll: the window opens into the whole view; the words step back quietly.
      var tl = gsap.timeline({
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom bottom', scrub: 0.6, invalidateOnRefresh: true, onRefresh: function () { measure(); draw(); } }
      });
      tl.to(state, { open: 1, duration: 1, ease: 'none', onUpdate: draw }, 0)
        .to(copy, { opacity: 0, y: -24, duration: 0.36, ease: 'power1.in' }, 0.06);
      if (schedule) tl.to(schedule, { opacity: 0, duration: 0.2 }, 0);
      if (overlay) {
        tl.fromTo(media, { '--ks-scrim': 0 }, { '--ks-scrim': 1, duration: 0.3, ease: 'none' }, 0.66)
          .fromTo(overlay, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' }, 0.72);
      }
    }, hero);
    contexts.set(hero, ctx);
    window.addEventListener('resize', function () { measure(); draw(); }, { passive: true });
  }

  /* Full-screen hero: the photograph arrives first, slowly, then a few quiet words. */
  function initHeroFull(hero, media, title, fades, copy) {
    var img = media.querySelector('img');
    if (!motion) { html.classList.remove('ks-boot'); return; }
    var ctx = gsap.context(function () {
      // The photo is visible from first paint (keeps LCP fast); it lifts from a dim, slightly close frame.
      var veil = media.querySelector('.ks-hero__veil');
      if (img) gsap.set(img, { opacity: 1, scale: 1.02 });
      if (veil) gsap.set(veil, { opacity: 0.3 });
      // Headline, label, text and buttons are revealed by CSS at first paint (micro motion, no JS wait).
      var play = function () {
        if (hero.__ksPlayed) return;
        hero.__ksPlayed = true;
        html.classList.remove('ks-boot');
        var tl = gsap.timeline();
        if (veil) tl.to(veil, { opacity: 0, duration: 0.6, ease: 'power2.out' }, 0);
        if (img) tl.to(img, { scale: 1, duration: 1.2, ease: 'expo.out', clearProps: 'will-change' }, 0);
      };
      var fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      var imgReady = img && img.decode ? img.decode().catch(function () {}) : Promise.resolve();
      Promise.race([Promise.all([fontsReady, imgReady]), new Promise(function (r) { setTimeout(r, 250); })]).then(function () { requestAnimationFrame(play); });

      // While the photograph sits under the header, the header turns light.
      // While the photograph sits under the header, the header turns light, and the browser's
      // top bar (iOS Safari status area / theme-color) takes the photo's top tone instead of the page beige.
      var wasUnder = null;
      var under = function () {
        var on = hero.getBoundingClientRect().bottom > 72;
        if (on === wasUnder) return;
        wasUnder = on;
        html.toggleAttribute('data-ks-hero-under', on);
        syncTop();
      };
      under();
      if (lenis) lenis.on('scroll', under); else window.addEventListener('scroll', under, { passive: true });

      // Scroll: the photograph drifts slower than the page; the words step back.
      if (img) gsap.to(media, { yPercent: 12, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      if (copy) gsap.to(copy, { opacity: 0, y: -30, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: '45% top', scrub: true } });
    }, hero);
    contexts.set(hero, ctx);
  }

  /* ---------------------------------------------------------------- section choreography */
  function initSections(root) {
    if (!motion) return;
    var ctx = gsap.context(function () {
      // Statement words wake as you read.
      $$('[data-ks-scrub-words]', root).forEach(function (el) {
        if (!window.SplitText) return;
        var split = SplitText.create(el, { type: 'words', wordsClass: 'ks-word', aria: 'none' });
        gsap.fromTo(split.words, { opacity: 0.32 }, {
          opacity: 1, ease: 'none', stagger: 0.1,
          scrollTrigger: { trigger: el, start: 'top 88%', end: 'top 40%', scrub: 0.2 }
        });
        var rush = $('[data-ks-rush]', el.parentElement);
        if (rush) {
          var lines = $$('.ks-manifesto__rush-line', rush);
          gsap.from(lines, { opacity: 0, x: -4, duration: 0.3, stagger: 0.04, ease: 'power2.out', scrollTrigger: { trigger: rush, start: 'top 85%' } });
          var strike = gsap.timeline({ scrollTrigger: { trigger: rush, start: 'top 70%', end: 'top 25%', scrub: 0.4 } });
          lines.forEach(function (ln, i) {
            strike.fromTo(ln, { '--strike': 0 }, { '--strike': 1, duration: 1, ease: 'power1.inOut' }, i * 0.55)
              .to(ln, { opacity: 0.42, duration: 0.6, ease: 'none' }, i * 0.55 + 0.6);
          });
        }
      });

      // Headings rise line by line.
      $$('[data-ks-lines]', root).forEach(function (el) {
        if (el.closest('[data-ks-hero]')) return;
        if (!window.SplitText) return;
        SplitText.create(el, {
          type: 'lines', mask: 'lines', autoSplit: true, linesClass: 'ks-line',
          onSplit: function (self) {
            return gsap.from(self.lines, { yPercent: 40, opacity: 0, duration: 0.55, stagger: 0.04, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 92%', once: true } });
          }
        });
      });

      // Images arrive through a softening frame.
      $$('[data-ks-reveal]', root).forEach(function (el) {
        var img = el.querySelector('img');
        gsap.fromTo(el, { clipPath: 'inset(3% 2% 3% 2% round 2px)' }, { clipPath: 'inset(0% 0% 0% 0% round 2px)', duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
        if (img) gsap.fromTo(img, { scale: 1.05 }, { scale: 1, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      });

      // Gentle depth inside frames.
      $$('[data-ks-parallax]', root).forEach(function (el) {
        var amt = parseFloat(el.getAttribute('data-ks-parallax')) || 10;
        gsap.fromTo(el, { yPercent: -amt / 2 }, { yPercent: amt / 2, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
      });

      // The hour arrives: digits run up to the moment.
      $$('[data-ks-tick]', root).forEach(function (el) {
        var target = toMinutes(el.getAttribute('data-ks-tick'));
        if (target === null) return;
        var o = { m: target - 95 };
        el.innerHTML = fmtMinutes(o.m, true);
        gsap.to(o, { m: target, duration: 0.8, ease: 'power3.out', snap: { m: 1 }, onUpdate: function () { el.innerHTML = fmtMinutes(o.m, true); }, scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
      });

      // Stills open from a framed print to the full view.
      $$('[data-ks-still]', root).forEach(function (el) {
        var img = $('[data-ks-still-img] img', el);
        var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 15%', scrub: true } });
        tl.fromTo(el, { '--ks-still-inset': '7vw', '--ks-still-r': '3px' }, { '--ks-still-inset': '0vw', '--ks-still-r': '0px', ease: 'none' }, 0);
        if (img) tl.fromTo(img, { '--ks-still-scale': 1.18 }, { '--ks-still-scale': 1, ease: 'none' }, 0);
      });

      // Full-screen scenes: the photo settles while the words arrive.
      $$('[data-ks-cinema]', root).forEach(function (sec) {
        var img = $('[data-ks-cinema-img]', sec);
        var title = $('[data-ks-cinema-title]', sec);
        var parts = $$('[data-ks-cinema-part]', sec);
        var tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: 'top top', end: 'bottom bottom', scrub: 0.6 } });
        if (img) tl.fromTo(img, { scale: 1.16, yPercent: 3 }, { scale: 1, yPercent: 0, ease: 'none', duration: 1 }, 0);
        var lines = [title];
        if (title && window.SplitText) lines = SplitText.create(title, { type: 'lines', mask: 'lines', linesClass: 'ks-line' }).lines;
        if (title) tl.fromTo(lines, { yPercent: 105 }, { yPercent: 0, ease: 'power2.out', duration: 0.22, stagger: 0.08 }, 0.12);
        if (parts.length) tl.fromTo(parts, { opacity: 0, y: 18 }, { opacity: 1, y: 0, ease: 'power1.out', duration: 0.2, stagger: 0.06 }, 0.42);
        // Entering: rise from a framed print into the full view.
        var stage = $('.ks-cinema__stage', sec);
        gsap.fromTo(stage, { clipPath: 'inset(8% 6% 0% 6% round 4px)' }, { clipPath: 'inset(0% 0% 0% 0% round 0px)', ease: 'none', scrollTrigger: { trigger: sec, start: 'top bottom', end: 'top top', scrub: true } });
      });

      // Studio prints drift slightly against the photograph.
      $$('.ks-moment__plate', root).forEach(function (el) {
        gsap.fromTo(el, { y: 60 }, { y: -40, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
      });

      // Product cards settle into the grid.
      $$('.ks-grid', root).forEach(function (grid) {
        gsap.from(grid.children, { y: 10, opacity: 0, duration: 0.5, stagger: 0.04, ease: 'power3.out', scrollTrigger: { trigger: grid, start: 'top 92%', once: true } });
      });
    }, root);
    contexts.set(root, ctx);
  }

  /* ---------------------------------------------------------------- cursor */
  function initCursor() {
    if (!motion || !finePointer || !M || !M.animate) return;
    var wrap = document.createElement('div');
    wrap.className = 'ks-cursor';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = '<div class="ks-cursor__ring"><span></span></div>';
    document.body.appendChild(wrap);
    var ring = wrap.firstChild, label = ring.firstChild;
    var qx = gsap.quickTo(wrap, 'x', { duration: 0.45, ease: 'power3.out' });
    var qy = gsap.quickTo(wrap, 'y', { duration: 0.45, ease: 'power3.out' });
    var current = null;
    window.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      qx(e.clientX); qy(e.clientY);
      var t = e.target.closest ? e.target.closest('[data-ks-cursor]') : null;
      var txt = t ? t.getAttribute('data-ks-cursor') : null;
      if (txt === current) return;
      current = txt;
      if (txt) {
        label.textContent = txt;
        M.animate(ring, { scale: 1, opacity: 1 }, { type: 'spring', stiffness: 300, damping: 24 });
      } else {
        M.animate(ring, { scale: 0, opacity: 0 }, { duration: 0.25, ease: [0.4, 0, 1, 1] });
      }
    }, { passive: true });
    document.addEventListener('pointerleave', function () { current = null; M.animate(ring, { scale: 0, opacity: 0 }, { duration: 0.2 }); });
  }

  /* ---------------------------------------------------------------- loupe */
  function initLoupe(root) {
    $$('[data-ks-loupe]', root).forEach(function (fig) {
      var lens = $('[data-ks-lens]', fig);
      if (!lens) return;
      var area = $('[data-ks-loupe-area]', fig) || fig;
      fig.setAttribute('data-ks-loupe-ready', '');
      var src = lens.getAttribute('data-ks-lens-src');
      if (src) {
        var load = function () { lens.style.backgroundImage = "url('" + src + "')"; };
        if ('IntersectionObserver' in window) {
          var lio = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { load(); lio.disconnect(); } }, { rootMargin: '600px 0px' });
          lio.observe(fig);
        } else load();
      }
      var zoom = parseFloat(fig.getAttribute('data-zoom')) || 2.5;
      var rest = { x: (parseFloat(fig.getAttribute('data-x')) || 55) / 100, y: (parseFloat(fig.getAttribute('data-y')) || 45) / 100 };
      var pos = { x: 0, y: 0 };
      var target = { x: 0, y: 0 };
      var active = false;
      var idleT = 0;
      var size = 0;
      var rect;

      function measure() {
        rect = area.getBoundingClientRect();
        size = lens.offsetWidth;
        lens.style.backgroundSize = (rect.width * zoom) + 'px ' + (rect.height * zoom) + 'px';
      }
      function render() {
        lens.style.setProperty('--lx', pos.x + 'px');
        lens.style.setProperty('--ly', pos.y + 'px');
        lens.style.backgroundPosition = (-(pos.x * zoom - size / 2)) + 'px ' + (-(pos.y * zoom - size / 2)) + 'px';
      }
      measure();
      pos.x = target.x = rect.width * rest.x;
      pos.y = target.y = rect.height * rest.y;
      render();

      var tick = function () {
        if (!active && motion) {
          idleT += 0.006;
          target.x = rect.width * (rest.x + Math.sin(idleT) * 0.06);
          target.y = rect.height * (rest.y + Math.sin(idleT * 1.7) * 0.05);
        }
        var k = active ? 0.22 : 0.05;
        pos.x += (target.x - pos.x) * k;
        pos.y += (target.y - pos.y) * k;
        render();
      };
      if (hasGsap) gsap.ticker.add(tick); else setInterval(tick, 16);

      var move = function (e) {
        rect = area.getBoundingClientRect();
        target.x = clamp(e.clientX - rect.left, 0, rect.width);
        target.y = clamp(e.clientY - rect.top, 0, rect.height);
      };
      area.addEventListener('pointerenter', function (e) {
        active = true; move(e);
        spring(lens, { scale: [0.82, 1] }, { stiffness: 420, damping: 26 });
      });
      area.addEventListener('pointermove', function (e) { active = true; move(e); });
      area.addEventListener('pointerdown', function (e) { active = true; move(e); });
      area.addEventListener('pointerleave', function () { active = false; });
      area.addEventListener('pointercancel', function () { active = false; });
      window.addEventListener('resize', measure, { passive: true });
    });
  }

  /* ---------------------------------------------------------------- accordions */
  function initAccordions(root) {
    $$('[data-ks-acc]', root).forEach(function (det) {
      if (det.__ks) return; det.__ks = true;
      var summary = $('summary', det);
      var panel = $('.ks-acc__panel', det);
      summary.addEventListener('click', function (e) {
        if (!panel || !panel.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        e.preventDefault();
        if (det.open) {
          var h = panel.offsetHeight;
          var a = panel.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 240, easing: 'cubic-bezier(0.65,0,0.35,1)' });
          a.onfinish = function () { det.open = false; if (window.ScrollTrigger) ScrollTrigger.refresh(); };
        } else {
          det.open = true;
          var end = panel.offsetHeight;
          panel.animate([{ height: '0px', opacity: 0 }, { height: end + 'px', opacity: 1 }], { duration: 320, easing: 'cubic-bezier(0.22,1,0.36,1)' }).onfinish = function () { if (window.ScrollTrigger) ScrollTrigger.refresh(); };
        }
      });
    });

    $$('[data-ks-questions]', root).forEach(function (sec) {
      var input = $('[data-ks-q-search]', sec);
      if (!input) return;
      var empty = $('[data-ks-q-empty]', sec);
      var items = $$('[data-ks-q-item]', sec);
      var groups = $$('[data-ks-q-group]', sec);
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var shown = 0;
        items.forEach(function (it) {
          var hit = !q || it.textContent.toLowerCase().indexOf(q) > -1;
          it.toggleAttribute('data-hidden-by-search', !hit);
          if (hit) shown++;
          if (q && hit && !it.open) it.open = q.length > 3;
        });
        groups.forEach(function (g) {
          var el = g.nextElementSibling, any = false;
          while (el && !el.hasAttribute('data-ks-q-group')) { if (el.hasAttribute('data-ks-q-item') && !el.hasAttribute('data-hidden-by-search')) any = true; el = el.nextElementSibling; }
          g.hidden = !any;
        });
        if (empty) empty.hidden = shown > 0;
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      });
    });
  }

  /* ---------------------------------------------------------------- small pleasures */
  function initDetails(root) {
    $$('[data-ks-turn]', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var on = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        spring(btn, { scale: [0.97, 1] }, { stiffness: 500, damping: 22 });
      });
    });

    if (motion && finePointer) {
      $$('[data-ks-magnetic]', root).forEach(function (el) {
        var qx = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3.out' });
        var qy = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' });
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          qx((e.clientX - (r.left + r.width / 2)) * 0.18);
          qy((e.clientY - (r.top + r.height / 2)) * 0.28);
        });
        el.addEventListener('pointerleave', function () { qx(0); qy(0); });
      });
    }

    $$('[data-ks-dialog-open]', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = document.getElementById(btn.getAttribute('data-ks-dialog-open'));
        if (!d) return;
        d.showModal();
        if (motion) gsap.fromTo(d, { xPercent: 100 }, { xPercent: 0, duration: 0.42, ease: 'power3.out' });
      });
    });
    $$('[data-ks-dialog]', root).forEach(function (d) {
      var close = function () {
        if (motion) gsap.to(d, { xPercent: 100, duration: 0.28, ease: 'power3.in', onComplete: function () { d.close(); gsap.set(d, { clearProps: 'transform' }); } });
        else d.close();
      };
      $$('[data-ks-dialog-close]', d).forEach(function (b) { b.addEventListener('click', close); });
      d.addEventListener('click', function (e) { if (e.target === d) close(); });
      d.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    });
  }

  /* ---------------------------------------------------------------- product page */
  function initProduct(root) {
    var pdp = $('[data-ks-pdp]', root);
    if (!pdp) return;
    var dataEl = $('[data-ks-product-json]', pdp);
    var data = dataEl ? JSON.parse(dataEl.textContent) : null;
    var form = $('[data-ks-product-form]', pdp);
    var idInput = form && $('[data-ks-variant-id]', form);
    var btn = $('[data-ks-add-main]', pdp);
    var btnText = btn && $('[data-ks-add-text]', btn);
    var btnPrice = btn && $('[data-ks-add-price]', btn);
    var priceEl = $('[data-ks-price-value]', pdp);
    var stickyBar = $('[data-ks-sticky-buy]', pdp);
    var stickyText = $('[data-ks-sticky-text]', pdp);
    var stickyPrice = $('[data-ks-sticky-price]', pdp);
    var segs = $$('[data-ks-seg]', pdp);

    function selectedOptions() {
      if (!data) return [];
      var out = [];
      for (var i = 0; i < data.options.length; i++) {
        var fixed = $('[data-ks-option-fixed="' + i + '"]', pdp);
        if (fixed) { out.push(fixed.value); continue; }
        var checked = $('[data-ks-option="' + i + '"] input:checked', pdp);
        out.push(checked ? checked.value : null);
      }
      return out;
    }

    function placeIndicator(seg, animate) {
      var ind = $('[data-ks-seg-ind]', seg);
      var checked = $('input:checked', seg);
      if (!ind) return;
      if (!checked) { seg.removeAttribute('data-has-value'); return; }
      var label = seg.querySelector('label[for="' + checked.id + '"]');
      var sr = seg.getBoundingClientRect(), lr = label.getBoundingClientRect();
      var x = lr.left - sr.left - 1, w = lr.width + 2;
      var had = seg.hasAttribute('data-has-value');
      var prevX = parseFloat(ind.dataset.x || x), prevW = parseFloat(ind.dataset.w || w);
      ind.dataset.x = x; ind.dataset.w = w;
      seg.setAttribute('data-has-value', '');
      if (animate && had && M && M.animate && motion) {
        M.animate(ind, { x: [prevX, x], width: [prevW + 'px', w + 'px'] }, { type: 'spring', stiffness: 420, damping: 34 });
      } else {
        ind.style.transform = 'translateX(' + x + 'px)';
        ind.style.width = w + 'px';
        if (animate && !had && motion && M && M.animate) M.animate(ind, { x: [x, x], opacity: [0, 1], scaleY: [0.6, 1] }, { type: 'spring', stiffness: 500, damping: 30 });
      }
    }

    function update(fromUser) {
      if (!data) return;
      var opts = selectedOptions();
      var complete = opts.every(function (o) { return o !== null; });
      var variant = complete ? data.variants.find(function (v) { return v.options.every(function (o, i) { return o === opts[i]; }); }) : null;

      // Mark which values are available given the other choices.
      $$('[data-ks-option]', pdp).forEach(function (fs) {
        var idx = parseInt(fs.getAttribute('data-ks-option'), 10);
        $$('input', fs).forEach(function (input) {
          var ok = data.variants.some(function (v) {
            return v.available && v.options[idx] === input.value && v.options.every(function (o, i) { return i === idx || opts[i] === null || opts[i] === o; });
          });
          input.toggleAttribute('data-unavailable', !ok);
        });
        var valueEl = $('[data-ks-option-value]', fs);
        if (valueEl) valueEl.textContent = opts[idx] ? String(opts[idx]).toUpperCase() : '';
      });

      if (idInput) idInput.value = variant ? variant.id : '';
      var statusEl = $('[data-ks-form-status]', pdp);
      if (complete && statusEl && fromUser) statusEl.textContent = '';
      var label, disabled = false;
      if (!complete) label = btn.getAttribute('data-label-choose');
      else if (!variant || !variant.available) { label = btn.getAttribute('data-label-sold'); disabled = true; }
      else label = btn.getAttribute('data-label-add');
      if (btn.getAttribute('data-state') !== 'busy') {
        btnText.textContent = label;
        btn.disabled = disabled;
      }
      if (stickyText) stickyText.textContent = label;
      if (variant) {
        var money = formatMoney(variant.price);
        if (priceEl) priceEl.textContent = money;
        if (btnPrice) btnPrice.textContent = money;
        if (stickyPrice) stickyPrice.textContent = money;
        if (fromUser) {
          var url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(history.state, '', url.toString());
        }
      }
    }

    segs.forEach(function (seg) {
      seg.addEventListener('change', function () { placeIndicator(seg, true); update(true); });
      placeIndicator(seg, false);
    });
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { segs.forEach(function (s) { placeIndicator(s, false); }); });
      segs.forEach(function (s) { ro.observe(s); });
    } else {
      window.addEventListener('resize', function () { segs.forEach(function (s) { placeIndicator(s, false); }); }, { passive: true });
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { segs.forEach(function (s) { placeIndicator(s, false); }); });
    update(false);

    document.addEventListener('ks:need-option', function (e) {
      if (!pdp.contains(e.target)) return;
      var missing = $$('[data-ks-option]', pdp).filter(function (fs) { return !$('input:checked', fs); })[0];
      if (!missing) return;
      var seg = $('[data-ks-seg]', missing);
      seg.removeAttribute('data-shake'); void seg.offsetWidth; seg.setAttribute('data-shake', '');
      var first = $('input:not([data-unavailable])', missing) || $('input', missing);
      if (first) first.focus({ preventScroll: true });
      if (missing.getBoundingClientRect().top < 80 || missing.getBoundingClientRect().bottom > innerHeight) scrollToTarget(missing);
    });

    // Gallery (mobile carousel position)
    var track = $('[data-ks-gallery-track]', pdp);
    var idxEl = $('[data-ks-gallery-index]', pdp);
    var bar = $('[data-ks-gallery-bar]', pdp);
    if (track && idxEl) {
      var total = track.children.length;
      var onTrack = function () {
        var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        idxEl.textContent = String(i + 1);
        if (bar) bar.parentElement.style.setProperty('--p', ((i + 1) / total * 100) + '%');
      };
      track.addEventListener('scroll', onTrack, { passive: true });
      onTrack();
    }

    // Lightbox
    var lb = $('[data-ks-lightbox]', pdp);
    if (lb) {
      var items = $$('[data-ks-lightbox-item]', lb);
      $$('[data-ks-zoom]', pdp).forEach(function (z) {
        z.addEventListener('click', function () {
          var i = parseInt(z.getAttribute('data-ks-zoom'), 10) || 0;
          lb.showModal();
          var target = items[i];
          if (target) lb.scrollTop = target.offsetTop - 12;
          if (motion) gsap.fromTo(lb, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
        });
      });
      var closeLb = function () {
        if (motion) gsap.to(lb, { opacity: 0, duration: 0.3, onComplete: function () { lb.close(); gsap.set(lb, { clearProps: 'opacity' }); } });
        else lb.close();
      };
      $('[data-ks-lightbox-close]', lb).addEventListener('click', closeLb);
      lb.addEventListener('cancel', function (e) { e.preventDefault(); closeLb(); });
    }

    // Sticky buy bar (mobile)
    if (stickyBar && btn && 'IntersectionObserver' in window) {
      var action = $('[data-ks-sticky-action]', stickyBar);
      var io = new IntersectionObserver(function (entries) {
        var visible = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        stickyBar.toggleAttribute('data-visible', visible);
        stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        action.tabIndex = visible ? 0 : -1;
      });
      io.observe(btn);
      action.addEventListener('click', function () {
        if (idInput && idInput.value) { if (form.requestSubmit) form.requestSubmit(btn); else btn.click(); }
        else { scrollToTarget($('[data-ks-option]', pdp) || form); setTimeout(function () { btn.dispatchEvent(new CustomEvent('ks:need-option', { bubbles: true })); }, 600); }
      });
    }
  }

  /* ---------------------------------------------------------------- footer */
  function initFooter() {
    var local = $('[data-ks-local-text]');
    if (local) {
      var render = function () {
        var d = new Date();
        var day = d.toLocaleDateString(undefined, { weekday: 'long' });
        var time = fmtMinutes(d.getHours() * 60 + d.getMinutes());
        local.textContent = 'It’s ' + day + ', ' + time + ' where you are.';
      };
      render();
      setInterval(render, 30000);
    }
    var toggle = $('[data-ks-motion-toggle]');
    if (toggle) {
      var on = html.classList.contains('ks-motion');
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      $('[data-ks-motion-label]', toggle).textContent = on ? 'Motion on' : 'Motion off';
      toggle.addEventListener('click', function () {
        try { localStorage.setItem('ks-motion', on ? 'off' : 'on'); } catch (e) {}
        location.reload();
      });
    }
  }

  /* ---------------------------------------------------------------- boot */
  // Above-the-fold work runs at once; everything below the fold waits for an idle moment,
  // one module per task, so no single long task blocks the first interaction.
  var idle = window.requestIdleCallback
    ? function (fn) { requestIdleCallback(fn, { timeout: 1200 }); }
    : function (fn) { setTimeout(fn, 200); };
  // One debounced ScrollTrigger refresh instead of several back-to-back full re-layouts at load.
  var refreshTimer = 0;
  function refreshSoon() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { if (hasGsap) ScrollTrigger.refresh(); if (typeof Day !== 'undefined') { Day.measure(); Day.update(); } }, 160);
  }
  function initRoot(root, deferred) {
    initHero(root);
    initProduct(root);
    initAccordions(root);
    if (!deferred) { initSections(root); initLoupe(root); initDetails(root); return; }
    idle(function () { initSections(root); refreshSoon(); });
    idle(function () { initLoupe(root); });
    idle(function () { initDetails(root); });
  }

  function boot() {
    if (hasGsap) {
      gsap.registerPlugin(ScrollTrigger);
      if (window.SplitText) gsap.registerPlugin(SplitText);
      gsap.config({ nullTargetWarn: false });
    }
    initLenis();
    initHeader();
    initRoot(document, true);
    Day.init();
    idle(initFooter);
    idle(initCursor);

    var onScroll = function () { Day.update(); };
    if (lenis) lenis.on('scroll', onScroll); else window.addEventListener('scroll', onScroll, { passive: true });
    var remeasure = function () { Day.measure(); Day.update(); };
    window.addEventListener('resize', remeasure, { passive: true });
    if (hasGsap) ScrollTrigger.addEventListener('refresh', remeasure);
    window.addEventListener('load', refreshSoon);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshSoon);
    if (!motion) html.classList.remove('ks-boot');

    // Theme editor support
    document.addEventListener('shopify:section:load', function (e) {
      initRoot(e.target);
      if (hasGsap) ScrollTrigger.refresh();
      remeasure();
    });
    document.addEventListener('shopify:section:unload', function (e) {
      contexts.forEach(function (ctx, el) { if (e.target.contains(el)) { ctx.revert(); contexts.delete(el); } });
    });

    KS.ready = true;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
